import { Form, FormInstance, Skeleton } from 'antd';
import { Store } from 'antd/lib/form/interface';
import React, { useEffect, useState } from 'react';

import { Banner, Text } from '@common/components';
import { useMutation } from '@common/hooks';
import { datetime } from '@common/services/datetime';
import { ComplianceItemUploadInput } from '@onboarding/components/compliance-item-files-list';
import { ComplianceItemInputExpirationDate } from '@onboarding/components/compliance-item-input-expiration-date';
import {
  ComplianceItemType,
  EXTRACT_DOCUMENT_INPUTS_MUTATION,
} from '@onboarding/graphql';
import {
  CreateDocumentMutation,
  UpdateDocumentMutation,
} from 'works/legacy/graphql/document';
import { formatToYearMonthDay } from 'works/legacy/lib/date-helper';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { createMessage } from 'works/legacy/lib/message-helper';

type Props = {
  form: FormInstance;
  complianceItem: ComplianceItemType;
  refetchQueries: string[];
  onSubmit?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
};

export function ComplianceItemAddAttachmentFormIdDocWithAi({
  form,
  complianceItem,
  refetchQueries,
  onSubmit,
  onSuccess,
  onError,
}: Props) {
  const { detailItem } = complianceItem;

  const errorMessage = () =>
    createMessage('error', 'Failed to update document. Please try again.');

  const [updateDocument, { loading: formSubmitting }] = useMutation(
    UpdateDocumentMutation,
    {
      refetchQueries,
      onCompleted: (response) => {
        if (response?.updateDocument.errors) {
          addErrorsToForm(form, response.updateDocument.errors);
          errorMessage();
          onError?.();
        } else {
          onSuccess?.();
          createMessage('success', 'Document has been successfully updated.');
        }
      },
      onError: () => {
        errorMessage();
        onError?.();
      },
    }
  );

  const [createDocument] = useMutation(CreateDocumentMutation, {
    refetchQueries,
    onCompleted: (response) => {
      if (response?.createDocument.errors) {
        addErrorsToForm(form, response.createDocument.errors);
        errorMessage();
        onError?.();
      } else {
        onSuccess?.();
        createMessage('success', 'Document has been successfully created.');
      }
    },
    onError: () => {
      errorMessage();
      onError?.();
    },
  });

  const [getDriversLicenseData, { loading: aiLoading, data }] = useMutation(
    EXTRACT_DOCUMENT_INPUTS_MUTATION
  );

  const filesField = Form.useWatch('files', form);
  const [hasExpirationError, setHasExpirationError] = useState(false);
  const [aiTraceId, setAiTraceId] = useState<string | null>(null);

  useEffect(() => {
    if (filesField?.fileList?.length) {
      getDriversLicenseData({
        variables: {
          documentFiles: filesField.fileList,
          documentType: 'driver_license',
          worksListId: complianceItem.worksList.id,
        },
      });
    }
  }, [complianceItem.worksList.id, filesField, getDriversLicenseData]);

  useEffect(() => {
    // TODO: the backend should return only an object with the right data
    // WORK-3234 - https://app.asana.com/1/303134204339906/project/1211544742839422/task/1212305560848436?focus=true
    if (data?.extractDocumentInputs?.extractedInputs?.length) {
      const dateValue =
        data.extractDocumentInputs.extractedInputs[0].inputValue;
      if (dateValue) {
        form.setFieldsValue({ expiresOn: datetime(dateValue) });
      } else {
        form.setFieldsValue({ expiresOn: null });
      }
      form
        .validateFields(['expiresOn'])
        .then(() => setHasExpirationError(false))
        .catch(() => setHasExpirationError(true));
    }

    if (data?.extractDocumentInputs?.aiTraceId) {
      setAiTraceId(data.extractDocumentInputs.aiTraceId);
    }
  }, [data?.extractDocumentInputs, form]);

  const onFinish = (values: Store) => {
    if (formSubmitting) return;

    if (!detailItem) {
      const initialDocType = complianceItem.documentTemplate?.key;
      const defaultTitle = `${
        complianceItem.documentTemplateSubtype || ''
      } ${complianceItem.documentTemplate?.name}`;

      createDocument({
        variables: {
          worksListItemId: complianceItem.id,
          worksProfileId:
            complianceItem.worksList.staffingRequestAssignment.worksProfile.id,
          input: {
            title: defaultTitle,
            expiresOn: formatToYearMonthDay(values.expiresOn),
            completedOn: formatToYearMonthDay(values.completedOn),
            docType: initialDocType,
          },
          files: values.files ? values.files.fileList : null,
          aiTraceId,
        },
      });

      return;
    }

    updateDocument({
      variables: {
        id: complianceItem.id,
        input: {
          completedOn: formatToYearMonthDay(values.completedOn),
          expiresOn: formatToYearMonthDay(values.expiresOn),
        },
        files: values.files?.fileList ?? [],
        aiTraceId,
      },
    });
  };

  const loading = aiLoading || formSubmitting;

  useEffect(() => {
    if (loading) {
      onSubmit?.();
    } else {
      onError?.();
    }
  }, [loading, onError, onSubmit]);

  const aiVerified = !aiLoading && filesField?.fileList?.length > 0;

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      {aiVerified && hasExpirationError && (
        <Banner type="warning">
          Data seems to be incorrect. Please double-check before saving.
        </Banner>
      )}

      {aiVerified && !hasExpirationError && (
        <Banner type="success">
          All data seems to be valid, please ensure accuracy before saving.
        </Banner>
      )}

      {aiLoading && <Text>File Selected: Checking...</Text>}

      <ComplianceItemUploadInput required disabled={aiLoading} />

      {aiLoading && <Skeleton.Input active block />}

      {aiVerified && (
        <ComplianceItemInputExpirationDate
          expirationType={complianceItem.expirationType}
        />
      )}
    </Form>
  );
}
